// eslint-disable-next-line no-unused-vars
const Types = require('../../../../jsdoc-types');

const {
  PAGE_NAMES,
  PRODUCT_TILE_LOCATION,
} = require('../../../../config/analytics');
const { EVENT_LABELS, STORAGE, TAG_DISPLAY_NAME } = require('./analyticsConst');
const { default: getFrontConfig } = require('../common/getFrontConfig');
const {
  getDoubleLookCodeFromPid,
  getDoubleLookPIDWithoutSize,
  isDoubleLook,
} = require('../common/utils/doubleLookUtils');
const { getNumericValue } = require('../common/utils/moneyUtils');
const { getPidWithoutSize } = require('../common/helpers/productIdHelpers');

const FRONT_CONFIG = getFrontConfig();

function buildProductSearchData(searchData) {
  return {
    dimension42: searchData.match, // search query match
    dimension43: searchData.query, // search query
    dimension44: searchData.count, // Search count
  };
}

/**
 * Get add to cart place data from localStorage
 * @returns {Object[]} add to cart place data
 */
function getDimensionsFromLocalStorage() {
  return JSON.parse(localStorage.getItem(STORAGE.local.dimensions)) || [];
}

/**
 * Get current grid column mode from session storage
 * @returns {string} The current column mode
 */
function getColumnModeFromSessionStorage() {
  const columnModeDefault = FRONT_CONFIG.vue.TheProductList.columnMode.default;
  const vuexStorage = sessionStorage.getItem('vuex');
  const columnMode = JSON.parse(vuexStorage)?.columnMode || columnModeDefault;
  return columnMode === columnModeDefault
    ? EVENT_LABELS.defaultView
    : EVENT_LABELS.compressedView;
}

/**
 * Handle add to cart place data
 * Get add to cart place data from localStorage or asign an empty array
 * If product already exists in stored add to cart place data, update info
 * If product does not exist in stored add to cart place data, create object
 * Set add to cart place data in localStorage
 * @param {string} pid product id added to cart
 * @param {string} dimensions the product dimensions
 */
function handleDimensionsData(pid, d38 = gaInfo.pageType) {
  const dimensionsData = getDimensionsFromLocalStorage();
  const dimensionsHit = dimensionsData?.find((hit) => hit.pid === pid);
  if (dimensionsHit) {
    dimensionsHit.d38 = d38;
    dimensionsHit.d45 = getColumnModeFromSessionStorage();
  } else {
    dimensionsData.push({
      pid,
      d38,
      d45: getColumnModeFromSessionStorage(),
    });
  }
  setDimensionsInLocalStorage(dimensionsData);
}

/**
 * Set localStorage add to cart place data
 * @param {Object[]} dimensionsData add to cart place Object array
 */
function setDimensionsInLocalStorage(dimensionsData) {
  localStorage.setItem(
    STORAGE.local.dimensions,
    JSON.stringify(dimensionsData)
  );
}

/**
 * Update gaInfo for cart_total_items and cart_total_value
 * @param {Object} cart cart data from event
 */
function updateCartGaInfo(cart) {
  gaInfo.cart_total_items = cart.numItems;
  gaInfo.cart_total_value = parseFloat(cart.totals.floatTotal).toFixed(2);
}

/**
 * Build product object to push in PDP and addToCart events
 * @param {Object} product full product info
 * @param {string} photoType identifier for photo type (D = PDP, _L = PLP, _P = PLAIN)
 * @param {string} list listName
 * @returns {Object} analytics eec product object
 */
function buildProduct(product, list) {
  if (!product) return {};
  const productID = product.doubleLookPID || product.id;
  const isProductDoubleLook = isDoubleLook(productID);
  return {
    id: getPidWithoutSize(productID),
    name: product.productName,
    category: product.parentCategoryName?.toUpperCase() || '',
    price: getNumericValueWithDecimals(product?.price?.sales?.decimalPrice),
    currencyCode: gaInfo.currencyCode,
    list,
    dimension4: getProductCollectionByPid(productID), // season
    dimension28: product.price?.list?.decimalPrice
      ? getNumericValueWithDecimals(product.price?.list?.decimalPrice)
      : getNumericValueWithDecimals(product?.price?.sales?.decimalPrice), // fullPrice
    dimension29: product.bylOnlineExclusive ? 'only online' : 'false', // onlyOnline
    dimension30: product.available ? 'yes' : 'no', // stockAvailability
    dimension31: product.price?.discount, // discount
    dimension32: getDoubleLookPIDWithoutSize(productID), // id just without size
    dimension33: isProductDoubleLook ? 'yes' : 'no', // doubleLook
    dimension34: isProductDoubleLook
      ? getDoubleLookCodeFromPid(productID)
      : 'no', // id doubleLook
  };
}

/**
 * Get an object with pairs variationID / displayValue from the array
 * of variation attributes of a product variant
 * @param {Types.VariationAttribute[]} variationAttributes variation attributes of
 * product variant of variationGroup
 * @returns {Object} { variationKey: variationValue }
 */
function getDisplayValuesOfVariationAttributes(variationAttributes) {
  return variationAttributes.reduce((obj, item) => {
    if (!item) return obj;
    const displayValue = item.values
      ? item.values.find((i) => i.selected === true)?.displayValue
      : item.displayValue;
    return {
      ...obj,
      [item.id]: displayValue,
    };
  }, {});
}

function getUserInfo() {
  return gaInfo.userInfo || {};
}

/**
 * Returns the part of the id corresponding to the collection. For 201BR2013.T1400, it returns 201.
 * @param  {string} pid - Pruduct id
 * @return {string} collection
 */
function getProductCollectionByPid(pid) {
  return pid?.split('.')[0].slice(0, -6);
}

/**
 * Gets the numeric Value (removes currency symbol), and sets decimals to 00.
 * Returns the value as a string
 * @param {string|int} value - The value to format
 * @returns {string|null} - The value formatted
 */
function getNumericValueWithDecimals(value) {
  const numericValue = value || 0;
  return getNumericValue(numericValue)?.toFixed(2);
}

/**
 * Retrieves the display name of a custom tag from a product object.
 *
 * @param {Object} product - The product object containing tag info.
 * @param {boolean} [product.bylOnlineExclusive] - If the product is online
 *    exclusive.
 * @param {boolean} [product.bylComingSoon] - If the product is coming soon.
 * @param {Object} [product.bylCustomTag] - Custom tag object.
 * @param {string} [product.bylCustomTag.name] - The name of the custom tag.
 * @returns {string} The tag name or a default value if no tag applies.
 * @throws {Error} If the product is null or undefined.
 */
function getProductTagName(product) {
  if (!product) {
    throw new Error('Invalid product: product is null or undefined');
  }

  let displayName = TAG_DISPLAY_NAME.undefined;

  if (product.bylOnlineExclusive) {
    displayName = TAG_DISPLAY_NAME.bylOnlineExclusive;
  } else if (product.bylComingSoon) {
    displayName = TAG_DISPLAY_NAME.bylComingSoon;
  } else if (product.bylCustomTag?.name) {
    displayName = product.bylCustomTag.name;
  }

  return displayName;
}

/**
 * Get the string that identifies all the filters and sorting option applied in
 * PLP.
 * Compatible with GA4 and UA.
 * @param {Object} [data] The filters selected
 * @param {ProductSearchRefinementValue[]} [data.selectedFilters] The filters selected
 * @param {SortingOption} [data.selectedSortingOption] The sorting option selected in
 * PLP
 * @returns {string} The string that identifies all the filters and sorting
 * option applied in PLP
 */
function parseSelectedFilters({
  selectedFilters = [],
  selectedSortingOption = null,
} = {}) {
  /**
   * Concat (separated by semicolon) the display values related to an attribute.
   * @param {ProductSearchRefinementValue[]} filters The selected filters
   * @param {string} attributeID The ID of the target attribute
   * @returns {string} Concatenated values
   */
  const parseSingleAttributeFilters = (filters, attributeID) =>
    filters
      .reduce((acc, filter) => {
        if (filter.id === attributeID) {
          acc.push(filter.displayValue);
        }
        return acc;
      }, [])
      .join(';');

  const sorting = selectedSortingOption?.displayName ?? '';
  const sizes = parseSingleAttributeFilters(selectedFilters, 'size');
  const colors = parseSingleAttributeFilters(
    selectedFilters,
    'bylUnifiedColor'
  );

  return `${sorting},${sizes},${colors}`;
}

/**
 * Returns the page name associated with a given product tile location.
 * @param {string} location - The Product Tile location
 * @returns {string} The corresponding page name
 */
function getPageNameFromProductTileLocation(location) {
  switch (location) {
    case PRODUCT_TILE_LOCATION.cartRecommendedProducts:
      return PAGE_NAMES.cart;
    case PRODUCT_TILE_LOCATION.emptyCartRecommendedProducts:
      return PAGE_NAMES.emptyCart;
    case PRODUCT_TILE_LOCATION.homeShopTheLook:
      return PAGE_NAMES.home;
    case PRODUCT_TILE_LOCATION.pdpCompleteTheLook:
    case PRODUCT_TILE_LOCATION.pdpRecommendedProducts:
      return PAGE_NAMES.pdp;
    case PRODUCT_TILE_LOCATION.plpGrid:
    case PRODUCT_TILE_LOCATION.plpSliderBanner:
      return PAGE_NAMES.plp;
    case PRODUCT_TILE_LOCATION.search:
    case PRODUCT_TILE_LOCATION.searchModal:
      return PAGE_NAMES.search;
    case PRODUCT_TILE_LOCATION.crossSellingGrid:
    default:
      return '';
  }
}

module.exports = {
  buildProduct,
  buildProductSearchData,
  getColumnModeFromSessionStorage,
  getDimensionsFromLocalStorage,
  getDisplayValuesOfVariationAttributes,
  getNumericValueWithDecimals,
  getPageNameFromProductTileLocation,
  getProductTagName,
  getUserInfo,
  handleDimensionsData,
  parseSelectedFilters,
  updateCartGaInfo,
};
